import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const type = params.get("type");
  const isMembership = type === "membership";
  const [status, setStatus] = useState("checking"); // checking | paid | error | timeout

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (e) { console.error("Payment status poll failed", e); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto px-5 py-20 text-center">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl border border-gray-100 shadow-soft p-10">
        {status === "checking" && (
          <><Loader2 className="animate-spin mx-auto mb-4 text-primary" size={48} />
          <h1 className="font-heading font-extrabold text-2xl text-[#1A1D20]">Confirming payment…</h1>
          <p className="text-[#6B7280] mt-2">Hang tight, this only takes a moment.</p></>
        )}
        {status === "paid" && (
          <><CheckCircle2 className="mx-auto mb-4 text-[#66C2A5]" size={56} />
          <h1 className="font-heading font-black text-3xl text-[#1A1D20]">{isMembership ? "Welcome to Pods!" : "Payment complete!"}</h1>
          <p className="text-[#6B7280] mt-2 mb-6">{isMembership ? "Your membership is active. Let's set up your first pod." : "Thanks for supporting your local runner."}</p>
          <Button data-testid="payment-dashboard-btn" onClick={() => navigate(isMembership ? "/pods" : "/dashboard")} className="rounded-full font-bold bg-primary hover:bg-[#FF8D70]">{isMembership ? "Set up my pod" : "Back to dashboard"}</Button></>
        )}
        {(status === "error" || status === "timeout") && (
          <><XCircle className="mx-auto mb-4 text-[#B23B3B]" size={56} />
          <h1 className="font-heading font-black text-2xl text-[#1A1D20]">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-[#6B7280] mt-2 mb-6">{status === "timeout" ? "Your payment is taking longer than usual. Check your dashboard shortly." : "We couldn't confirm your payment."}</p>
          <Link to="/dashboard"><Button className="rounded-full font-bold bg-primary hover:bg-[#FF8D70]">Go to dashboard</Button></Link></>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto px-5 py-20 text-center">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-10">
        <XCircle className="mx-auto mb-4 text-[#6B7280]" size={56} />
        <h1 className="font-heading font-black text-2xl text-[#1A1D20]">Payment cancelled</h1>
        <p className="text-[#6B7280] mt-2 mb-6">No worries — you can pay anytime from the errand page.</p>
        <Link to="/dashboard"><Button data-testid="cancel-dashboard-btn" className="rounded-full font-bold bg-primary hover:bg-[#FF8D70]">Back to dashboard</Button></Link>
      </div>
    </div>
  );
}
